import AsyncStorage from '@react-native-async-storage/async-storage';
import {create} from 'zustand';
import {persist, createJSONStorage} from 'zustand/middleware';
import {calculatePriceBasedOnQuantity} from '../utils/price';

export interface ProductInCart extends IProduct {
  quantity: number;
  preco: number;
  discountedPrice: number;
  cartKey?: string; // sku + '_promo' for promotional entries; defaults to sku
}

const getKey = (product: ProductInCart) => product.cartKey ?? product.sku;

/** Sacola esvaziada pelo cliente deixa de ser do pedido sugerido. */
const withEmptyCartReset = (products: ProductInCart[]) =>
  products.length === 0 ? {products, suggestedFillId: null} : {products};

interface CartState {
  products: ProductInCart[];
  /**
   * Id do retrato do pedido sugerido usado para encher esta sacola
   * (`SuggestedFillService`). Vai no checkout em `X-Suggested-Fill-Id`. Mora
   * aqui, e não num store à parte, para ser salvo e limpo junto com a sacola.
   */
  suggestedFillId: string | null;
  setSuggestedFillId: (id: string | null) => void;
  addProduct: (product: ProductInCart) => void;
  removeProduct: (cartKey: string) => void;
  updateProductQuantity: (cartKey: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

export const useBagStore = create(
  persist<CartState>(
    (set, get) => ({
      products: [],
      suggestedFillId: null,

      setSuggestedFillId: (id: string | null) => set({suggestedFillId: id}),

      addProduct: (newProduct: ProductInCart) => {
        const {products} = get();
        const key = getKey(newProduct);
        const existingProduct = products.find(p => getKey(p) === key);

        const {price, discountedPrice} = calculatePriceBasedOnQuantity(
          newProduct,
          newProduct.quantity,
        );

        if (existingProduct) {
          set({
            products: products.map(product =>
              getKey(product) === key
                ? {
                    ...product,
                    quantity: product.quantity + newProduct.quantity,
                    preco: price,
                    discountedPrice,
                  }
                : product,
            ),
          });
        } else {
          set({
            products: [
              ...products,
              {
                ...newProduct,
                preco: price,
                discountedPrice,
              },
            ],
          });
        }
      },

      removeProduct: (cartKey: string) => {
        set(withEmptyCartReset(
          get().products.filter(p => getKey(p) !== cartKey),
        ));
      },

      updateProductQuantity: (cartKey: string, quantity: number) => {
        const {products} = get();
        const existingProduct = products.find(p => getKey(p) === cartKey);

        if (existingProduct) {
          if (quantity > 0) {
            const {price, discountedPrice} = calculatePriceBasedOnQuantity(
              existingProduct,
              quantity,
            );
            set({
              products: products.map(product =>
                getKey(product) === cartKey
                  ? {
                      ...product,
                      quantity,
                      preco: price,
                      discountedPrice,
                    }
                  : product,
              ),
            });
          } else {
            set(withEmptyCartReset(
              products.filter(p => getKey(p) !== cartKey),
            ));
          }
        }
      },

      clearCart: () => set({products: [], suggestedFillId: null}),

      getTotalItems: () => {
        return get().products.reduce(
          (total, product) => total + product.quantity,
          0,
        );
      },

      getTotalPrice: () => {
        return get().products.reduce((total, product) => {
          const precoNumerico = product.discountedPrice
            ? parseFloat(product.discountedPrice.toString().replace(',', '.'))
            : parseFloat(product.preco.toString().replace(',', '.'));

          return total + precoNumerico * product.quantity;
        }, 0);
      },
    }),
    {
      name: 'cart_store',
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
);
